import { Button, Checkbox, Icon, Modal, createExitHold, useToast } from '@/components';
import { useAuth } from '@/features/auth/AuthProvider';
import type { Changelog, ReleaseDetail, ReleaseVersionGroup } from '@uchat-team-management/shared';
import { For, Show, createMemo, createSignal } from 'solid-js';
import {
  type Line,
  SUMMARY_SEPARATOR,
  type StatusMessage,
  buildStatusItems,
  composeStatusMessage,
  defaultMessage,
  versionRound,
} from './build-status-message';
import { useReleaseChangelog, useReleaseVersions } from './hooks';

/** text/html + text/plain together so rich chats get clickable links; plain inputs fall back. */
const copyToClipboard = async (message: StatusMessage): Promise<void> => {
  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard.write) {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/plain': new Blob([message.text], { type: 'text/plain' }),
        'text/html': new Blob([message.html], { type: 'text/html' }),
      }),
    ]);
  } else {
    await navigator.clipboard.writeText(message.text);
  }
};

/**
 * Preview dialog (UCH-423): the Message line renders as text — hover tints it,
 * click to edit — between the header block and the change details. Each
 * detail bullet carries a checkbox (UCH-514) so only checked items are copied.
 */
const CopySummaryModal = (props: {
  open: boolean;
  onClose: () => void;
  release: ReleaseDetail;
  changelog: Changelog;
  versions: ReleaseVersionGroup[];
}) => {
  const toast = useToast();
  const { session } = useAuth();
  const [message, setMessage] = createSignal(defaultMessage(props.release.status));
  const items = createMemo(() => buildStatusItems(props.changelog));

  // UCH-514 amendment: "mine" = bullets authored by my GitHub username
  // (case-insensitive — GitHub logins are). The modal remounts per open, so
  // these are stable for its lifetime.
  const me = session()?.githubUsername?.toLowerCase() ?? null;
  const isMine = (line: Line) => me !== null && line.authorLogin?.toLowerCase() === me;
  const keysWhere = (pred: (line: Line) => boolean): ReadonlySet<string> =>
    new Set([
      ...items().readyItems.flatMap((l, i) => (pred(l) ? [`r${i}`] : [])),
      ...items().manualItems.flatMap((l, i) => (pred(l) ? [`m${i}`] : [])),
    ]);

  // Keys are `<section><index>`. Pre-select only my bullets when I have a
  // GitHub username and at least one bullet is mine; otherwise everything.
  const [excluded, setExcluded] = createSignal<ReadonlySet<string>>(
    keysWhere(isMine).size > 0 ? keysWhere((l) => !isMine(l)) : new Set(),
  );
  const setIncluded = (key: string, included: boolean) => {
    const next = new Set(excluded());
    included ? next.delete(key) : next.add(key);
    setExcluded(next);
  };

  const built = () =>
    composeStatusMessage(
      {
        productName: props.release.productName,
        environment: props.release.environment,
        version: props.release.version,
        buildNumber: props.release.buildNumber,
        status: props.release.status,
        round: versionRound(props.versions, props.release.version, props.release.buildNumber),
        message: message(),
      },
      {
        readyItems: items().readyItems.filter((_, i) => !excluded().has(`r${i}`)),
        manualItems: items().manualItems.filter((_, i) => !excluded().has(`m${i}`)),
      },
    );

  const copy = async () => {
    try {
      await copyToClipboard(built());
      toast.success('Copied');
      props.onClose();
    } catch {
      toast.error('Copy failed');
    }
  };

  const itemSection = (heading: string, lines: Line[], keyPrefix: string) => (
    <Show when={lines.length > 0}>
      <div class="mt-3">
        <p class="font-semibold">{heading}</p>
        <ul class="mt-1 flex flex-col gap-1">
          <For each={lines}>
            {(line, i) => (
              <li>
                <Checkbox
                  checked={!excluded().has(`${keyPrefix}${i()}`)}
                  onChange={(checked) => setIncluded(`${keyPrefix}${i()}`, checked)}
                  class="items-start"
                  label={<span class="whitespace-pre-wrap font-mono text-sm">{line.text}</span>}
                />
              </li>
            )}
          </For>
        </ul>
      </div>
    </Show>
  );

  return (
    <Modal open={props.open} onClose={props.onClose} title="Copy Summary" size="lg">
      <div class="flex flex-col gap-3">
        <div class="max-h-96 overflow-y-auto rounded-lg border border-neutral-200 p-3 font-mono text-sm dark:border-neutral-800">
          <div class="whitespace-pre-wrap">{built().headerText}</div>
          <div aria-hidden class="text-neutral-400">
            {SUMMARY_SEPARATOR}
          </div>
          <input
            value={message()}
            onInput={(e) => setMessage(e.currentTarget.value)}
            aria-label="Message"
            class="-mx-1 my-2 w-full rounded bg-transparent px-1 font-mono text-sm transition-colors hover:bg-neutral-100 focus:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-brand-500 motion-reduce:transition-none dark:hover:bg-neutral-800 dark:focus:bg-neutral-800"
          />
          <Show when={items().readyItems.length > 0 || items().manualItems.length > 0}>
            <div aria-hidden class="text-neutral-400">
              {SUMMARY_SEPARATOR}
            </div>
            {itemSection('Ready for QA:', items().readyItems, 'r')}
            {itemSection('Manual review (no ticket / not found):', items().manualItems, 'm')}
          </Show>
        </div>
        <div class="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => setExcluded(new Set())}>
            <Icon name="checklist-plan" />
            Select all
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setExcluded(keysWhere(() => true))}>
            <Icon name="cross-small" />
            Unselect all
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              if (!me) {
                toast.info('Set your GitHub username on your Profile page first');
                return;
              }
              setExcluded(keysWhere((l) => !isMine(l)));
            }}
          >
            <Icon name="check" />
            Select me
          </Button>
          <div class="ml-auto flex gap-2">
            <Button variant="ghost" onClick={props.onClose}>
              Cancel
            </Button>
            <Button onClick={copy}>Copy</Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

/**
 * Controlled summary preview dialog (queries live here); opened from the
 * Actions menu (UCH-440). Only mounted on non-draft releases. Opened before
 * the changelog loads → the dialog appears once the data arrives.
 */
export const CopySummary = (props: {
  release: ReleaseDetail;
  open: boolean;
  onClose: () => void;
}) => {
  const changelog = useReleaseChangelog(() => ({
    releaseId: props.release.id,
    against: 'baseline',
  }));
  const versions = useReleaseVersions(() => ({
    productId: props.release.productId,
    environment: props.release.environment,
  }));
  // Keep the modal mounted (with its changelog) through the exit animation after close.
  const heldChangelog = createExitHold(() => (props.open && versions.data ? changelog.data : null));

  return (
    <Show when={heldChangelog()}>
      {(log) => (
        <CopySummaryModal
          open={props.open}
          onClose={props.onClose}
          release={props.release}
          changelog={log().data}
          versions={versions.data?.data ?? []}
        />
      )}
    </Show>
  );
};
